import { z } from "zod";
import { PayoutKindSchema } from "./common";

export const PayoutStatusSchema = z.enum([
  "draft",
  "pending_approval",
  "approved",
  "paid",
  "cancelled",
]);
export type PayoutStatusDTO = z.infer<typeof PayoutStatusSchema>;

export const PayoutRecipientSchema = z.object({
  id: z.string(),
  payoutId: z.string(),
  performerId: z.string().nullable(),
  performerName: z.string().nullable().optional(),
  eventStaffId: z.string().nullable(),
  eventOutsourceId: z.string().nullable().optional(),
  staffName: z.string().nullable().optional(),
  staffRoleLabel: z.string().nullable().optional(),
  bankName: z.string().nullable(),
  bankAccount: z.string().nullable(),
  accountName: z.string().nullable(),
  subtotalNet: z.number(),
  sortOrder: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type PayoutRecipientDTO = z.infer<typeof PayoutRecipientSchema>;

export const PayoutLineSchema = z.object({
  id: z.string(),
  payoutId: z.string(),
  assignmentId: z.string().nullable(),
  performerId: z.string().nullable(),
  performerName: z.string().nullable().optional(),
  eventStaffId: z.string().nullable(),
  staffName: z.string().nullable().optional(),
  description: z.string(),
  quantity: z.number(),
  unit: z.string().nullable(),
  unitAmount: z.number(),
  grossAmount: z.number(),
  adjustmentAmount: z.number(),
  withholdingTax: z.number(),
  netAmount: z.number(),
  notes: z.string().nullable(),
  sortOrder: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type PayoutLineDTO = z.infer<typeof PayoutLineSchema>;

export const PayoutSchema = z.object({
  id: z.string(),
  payoutNo: z.string(),
  status: PayoutStatusSchema,
  payoutKind: PayoutKindSchema,
  eventOutsourceId: z.string().nullable().optional(),
  eventId: z.string(),
  eventTitle: z.string().nullable().optional(),
  eventDate: z.coerce.date().nullable().optional(),
  performerId: z.string().nullable(),
  performerName: z.string().nullable().optional(),
  isBatch: z.boolean(),
  isStaffPayout: z.boolean(),
  isProductionPayout: z.boolean(),
  isOutsourcePayout: z.boolean(),
  performerCount: z.number().optional(),
  recipientCount: z.number().optional(),
  totalAmount: z.number(),
  bankName: z.string().nullable(),
  bankAccount: z.string().nullable(),
  accountName: z.string().nullable(),
  notes: z.string().nullable(),
  pdfFileId: z.string().nullable(),
  paymentProofFileId: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.coerce.date().nullable(),
  paidBy: z.string().nullable(),
  paidAt: z.coerce.date().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  recipients: z.array(PayoutRecipientSchema).optional(),
  lines: z.array(PayoutLineSchema).optional(),
});
export type PayoutDTO = z.infer<typeof PayoutSchema>;

export const PayoutListItemSchema = PayoutSchema.omit({
  lines: true,
  recipients: true,
}).extend({
  eventTitle: z.string().nullable(),
  eventDate: z.coerce.date().nullable(),
  performerName: z.string().nullable(),
  performerCount: z.number(),
});
export type PayoutListItemDTO = z.infer<typeof PayoutListItemSchema>;

export const CreatePayoutSchema = z.object({});
export type CreatePayoutDTO = z.infer<typeof CreatePayoutSchema>;

export const StaffPayoutLineItemSchema = z.object({
  eventStaffId: z.string().min(1),
  netAmount: z.number().int().positive("ຍອດຕ້ອງຫຼາຍກວ່າ 0"),
  description: z.string().trim().optional(),
  withholdingTax: z.number().int().min(0).optional(),
  adjustmentAmount: z.number().int().optional(),
});

export const CreateStaffPayoutSchema = z.object({
  staffLineItems: z.array(StaffPayoutLineItemSchema).min(1),
  notes: z.string().trim().optional(),
});
export type CreateStaffPayoutDTO = z.infer<typeof CreateStaffPayoutSchema>;

export const CreateProductionPayoutSchema = z.object({
  costLineIds: z.array(z.string().min(1)).min(1, "ເລືອກຢ່າງນ້ອຍ 1 ລາຍການ"),
  notes: z.string().trim().optional(),
});
export type CreateProductionPayoutDTO = z.infer<
  typeof CreateProductionPayoutSchema
>;

export const UpdatePayoutRecipientSchema = z.object({
  id: z.string().optional(),
  performerId: z.string().min(1).optional(),
  eventStaffId: z.string().min(1).optional(),
  bankName: z.string().nullable().optional(),
  bankAccount: z.string().nullable().optional(),
  accountName: z.string().nullable().optional(),
  sortOrder: z.number().int().optional(),
});
export type UpdatePayoutRecipientDTO = z.infer<
  typeof UpdatePayoutRecipientSchema
>;

export const UpdatePayoutLineSchema = z.object({
  id: z.string().optional(),
  assignmentId: z.string().nullable().optional(),
  performerId: z.string().nullable().optional(),
  eventStaffId: z.string().nullable().optional(),
  description: z.string().trim().min(1),
  quantity: z.number().int().min(1),
  unit: z.string().nullable().optional(),
  unitAmount: z.number().int().min(0),
  adjustmentAmount: z.number().int().optional(),
  withholdingTax: z.number().int().min(0).optional(),
  notes: z.string().nullable().optional(),
  sortOrder: z.number().int().optional(),
});
export type UpdatePayoutLineDTO = z.infer<typeof UpdatePayoutLineSchema>;

export const UpdatePayoutSchema = z.object({
  bankName: z.string().nullable().optional(),
  bankAccount: z.string().nullable().optional(),
  accountName: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  recipients: z.array(UpdatePayoutRecipientSchema).optional(),
  lines: z.array(UpdatePayoutLineSchema).optional(),
});
export type UpdatePayoutDTO = z.infer<typeof UpdatePayoutSchema>;

export const MarkPayoutPaidSchema = z.object({
  paymentProofFileId: z.string().nullable().optional(),
});
export type MarkPayoutPaidDTO = z.infer<typeof MarkPayoutPaidSchema>;

export const SetPayoutPaymentProofSchema = z.object({
  paymentProofFileId: z.string().min(1),
});
export type SetPayoutPaymentProofDTO = z.infer<
  typeof SetPayoutPaymentProofSchema
>;

export const CancelPayoutSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type CancelPayoutDTO = z.infer<typeof CancelPayoutSchema>;

export const PayoutIdParamSchema = z.object({
  payoutId: z.string().min(1),
});

export const PayoutEventParamSchema = z.object({
  eventId: z.string().min(1),
  payoutId: z.string().min(1),
});
